import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Search, ArrowLeft, Edit, Trash2, Copy,
  ChevronDown, ChevronRight, Folder, FileText,
  Globe, Check, MoreVertical, Sparkles, X, Wand2,
  Lightbulb, MessageSquare
} from 'lucide-react';
import { AIAssistant, useAIAssistant } from '../components/AIAssistant';
import { AIClauseSuggestions } from '../components/AIClauseSuggestions';
import { AIFloatingButton } from '../components/AIFloatingButton';
import { CommandPalette, useCommandPalette } from '../components/CommandPalette';

// Mock clause data
const mockClauses = [
  {
    id: 'warranty_covenant',
    name: 'Warranty Covenant',
    category: 'Real Estate',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard warranty covenant for deed transfers',
    content: 'The Grantor hereby covenants with the Grantee that the Grantor is lawfully seized in fee simple of the above-described premises...',
    usageCount: 45,
    lastModified: new Date('2024-12-01'),
  },
  {
    id: 'quitclaim_covenant',
    name: 'Quitclaim Covenant',
    category: 'Real Estate',
    jurisdictions: ['WI', 'FL'],
    description: 'Quitclaim covenant with no warranties',
    content: 'The Grantor conveys and quitclaims to the Grantee all interest which the Grantor may have in the above-described premises...',
    usageCount: 32,
    lastModified: new Date('2024-11-28'),
  },
  {
    id: 'homestead_waiver',
    name: 'Homestead Waiver',
    category: 'Real Estate',
    jurisdictions: ['FL'],
    description: 'Florida homestead rights waiver',
    content: 'The undersigned spouse hereby waives any and all homestead rights in and to the above-described property...',
    usageCount: 18,
    lastModified: new Date('2024-12-02'),
  },
  {
    id: 'marital_property_waiver',
    name: 'Marital Property Waiver',
    category: 'Real Estate',
    jurisdictions: ['WI'],
    description: 'Wisconsin Chapter 766 marital property waiver',
    content: 'The undersigned spouse hereby waives any interest in the above-described property pursuant to Wisconsin Statutes Chapter 766...',
    usageCount: 22,
    lastModified: new Date('2024-11-30'),
  },
  {
    id: 'notary_acknowledgment',
    name: 'Notary Acknowledgment',
    category: 'General',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard notary acknowledgment block',
    content: 'STATE OF {{state}}\nCOUNTY OF {{county}}\n\nThis instrument was acknowledged before me on {{date}}...',
    usageCount: 128,
    lastModified: new Date('2024-12-04'),
  },
  {
    id: 'self_proving_affidavit',
    name: 'Self-Proving Affidavit',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Affidavit for self-proving will',
    content: 'We, the undersigned witnesses, being duly sworn, declare to the undersigned authority that the testator...',
    usageCount: 15,
    lastModified: new Date('2024-12-03'),
  },
  {
    id: 'no_contest_clause',
    name: 'No Contest Clause',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'In terrorem clause for wills and trusts',
    content: 'If any beneficiary under this Will shall contest or attack this Will or any of its provisions...',
    usageCount: 12,
    lastModified: new Date('2024-11-25'),
  },
  {
    id: 'llc_purpose',
    name: 'LLC Purpose Statement',
    category: 'Business',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard LLC purpose clause',
    content: 'The purpose of the Company is to engage in any lawful act or activity for which limited liability companies may be organized...',
    usageCount: 28,
    lastModified: new Date('2024-12-01'),
  },
  {
    id: 'indemnification',
    name: 'Indemnification Clause',
    category: 'Business',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard member/manager indemnification',
    content: 'The Company shall indemnify and hold harmless each Member and Manager from and against any and all claims...',
    usageCount: 35,
    lastModified: new Date('2024-11-29'),
  },
];

const categories = ['All', 'Real Estate', 'Business', 'Estate Planning', 'General'];

export function ClauseLibrary() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedClause, setSelectedClause] = useState<typeof mockClauses[0] | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [clauses, setClauses] = useState(mockClauses);

  // AI Feature States
  const aiAssistant = useAIAssistant();
  const commandPalette = useCommandPalette();
  const [showClauseSuggestions, setShowClauseSuggestions] = useState(false);

  const filteredClauses = clauses.filter(c => {
    if (selectedCategory !== 'All' && c.category !== selectedCategory) return false;
    if (searchQuery && !c.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  // Group by category
  const clausesByCategory = filteredClauses.reduce((acc, clause) => {
    if (!acc[clause.category]) acc[clause.category] = [];
    acc[clause.category].push(clause);
    return acc;
  }, {} as Record<string, typeof mockClauses>);

  // Handle AI-suggested clause insertion
  const handleInsertClause = (content: string, name: string) => {
    const newClause = {
      id: name.toLowerCase().replace(/\s+/g, '_'),
      name,
      category: selectedCategory === 'All' ? 'General' : selectedCategory,
      jurisdictions: ['WI', 'FL'],
      description: 'AI-generated clause',
      content,
      usageCount: 0,
      lastModified: new Date(),
    };
    setClauses([...clauses, newClause]);
    setSelectedClause(newClause);
    setShowClauseSuggestions(false);
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-primary-900 via-[#1a1f2e] to-primary-800">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-primary-700/50 bg-primary-900/50 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <Link
            to="/templates"
            className="flex items-center gap-2 text-primary-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
              <Folder className="w-5 h-5 text-primary-900" />
            </div>
            <div>
              <h1 className="text-xl font-display font-semibold text-white">Clause Library</h1>
              <p className="text-sm text-primary-400">{clauses.length} clauses</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* AI Suggest Button */}
          <button
            onClick={() => setShowClauseSuggestions(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-600 text-white font-medium rounded-lg hover:from-pink-600 hover:to-rose-700 transition-all shadow-lg shadow-pink-500/25"
          >
            <Lightbulb className="w-4 h-4" />
            AI Suggest
          </button>
          
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Clause
          </button>
        </div>
      </header>
      
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel - clause list */}
        <div className="w-96 border-r border-primary-700/50 flex flex-col">
          {/* Search and filter */}
          <div className="p-4 space-y-3 border-b border-primary-700/50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search clauses..."
                className="w-full pl-9 pr-4 py-2 bg-primary-800/50 border border-primary-600/50 rounded-lg text-sm text-white placeholder-primary-400 focus:outline-none focus:border-accent-500/50"
              />
            </div>
            
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    selectedCategory === cat
                      ? 'bg-accent-500/20 text-accent-400 border border-accent-500/50'
                      : 'bg-primary-800/50 text-primary-400 border border-primary-600/50 hover:text-white'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          
          {/* Clause list */}
          <div className="flex-1 overflow-auto p-4">
            <div className="space-y-6">
              {Object.entries(clausesByCategory).map(([category, categoryClauses]) => (
                <div key={category}>
                  <div className="flex items-center gap-2 mb-2">
                    <Folder className="w-4 h-4 text-primary-400" />
                    <span className="text-xs font-medium text-primary-300 uppercase tracking-wider">
                      {category}
                    </span>
                    <span className="text-xs text-primary-500">({categoryClauses.length})</span>
                  </div>
                  
                  <div className="space-y-1 ml-6">
                    {categoryClauses.map(clause => (
                      <button
                        key={clause.id}
                        onClick={() => setSelectedClause(clause)}
                        className={`w-full text-left p-3 rounded-lg transition-all ${
                          selectedClause?.id === clause.id
                            ? 'bg-accent-500/20 border border-accent-500/50'
                            : 'bg-primary-800/30 border border-transparent hover:bg-primary-800/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-white">{clause.name}</span>
                          <div className="flex gap-1">
                            {clause.jurisdictions.map(j => (
                              <span key={j} className="badge badge-accent text-xs">
                                {j}
                              </span>
                            ))}
                          </div>
                        </div>
                        <p className="text-xs text-primary-400 mt-1 line-clamp-1">
                          {clause.description}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {filteredClauses.length === 0 && (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto text-primary-600 mb-3" />
                  <p className="text-primary-400">No clauses found</p>
                  <button
                    onClick={() => setShowClauseSuggestions(true)}
                    className="mt-3 flex items-center gap-2 mx-auto px-3 py-1.5 text-sm text-pink-400 hover:bg-pink-500/10 rounded-lg transition-colors"
                  >
                    <Lightbulb className="w-4 h-4" />
                    Get AI suggestions
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Right panel - clause detail */}
        <div className="flex-1 overflow-auto">
          {selectedClause ? (
            <div className="p-6">
              <div className="max-w-3xl">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-display font-semibold text-white mb-2">
                      {selectedClause.name}
                    </h2>
                    <p className="text-primary-300">{selectedClause.description}</p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button className="btn-secondary flex items-center gap-2">
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button className="btn-ghost">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Meta */}
                <div className="flex items-center gap-6 mb-6 p-4 bg-primary-800/30 rounded-lg">
                  <div>
                    <span className="text-xs text-primary-400 block mb-1">Category</span>
                    <span className="text-sm text-white">{selectedClause.category}</span>
                  </div>
                  <div>
                    <span className="text-xs text-primary-400 block mb-1">Jurisdictions</span>
                    <div className="flex gap-1">
                      {selectedClause.jurisdictions.map(j => (
                        <span key={j} className="badge badge-accent">
                          {j}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-primary-400 block mb-1">Used In</span>
                    <span className="text-sm text-white">{selectedClause.usageCount} templates</span>
                  </div>
                  <div>
                    <span className="text-xs text-primary-400 block mb-1">Last Modified</span>
                    <span className="text-sm text-white">
                      {selectedClause.lastModified.toLocaleDateString()}
                    </span>
                  </div>
                </div>
                
                {/* Usage */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-primary-300 mb-2">Usage</h3>
                  <div className="flex items-center gap-2 p-3 bg-primary-800/30 rounded-lg">
                    <code className="flex-1 text-sm font-mono text-accent-400">
                      {'{{@clause:' + selectedClause.id + '}}'}
                    </code>
                    <button
                      onClick={() => navigator.clipboard.writeText(`{{@clause:${selectedClause.id}}}`)}
                      className="p-2 hover:bg-white/10 rounded transition-colors"
                    >
                      <Copy className="w-4 h-4 text-primary-400" />
                    </button>
                  </div>
                  <p className="text-xs text-primary-500 mt-2">
                    Add jurisdiction: {'{{@clause:' + selectedClause.id + ':WI}}'}
                  </p>
                </div>
                
                {/* Content */}
                <div>
                  <h3 className="text-sm font-medium text-primary-300 mb-2">Content</h3>
                  <div className="p-4 bg-primary-900/50 border border-primary-700/50 rounded-lg">
                    <pre className="text-sm text-primary-200 whitespace-pre-wrap font-mono">
                      {selectedClause.content}
                    </pre>
                  </div>
                </div>
                
                {/* Jurisdiction variants */}
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-primary-300 mb-2">Jurisdiction Variants</h3>
                  <div className="space-y-3">
                    {selectedClause.jurisdictions.map(j => (
                      <div key={j} className="p-4 bg-primary-800/30 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="badge badge-accent">{j}</span>
                          <button className="text-xs text-accent-400 hover:underline">
                            Edit variant
                          </button>
                        </div>
                        <p className="text-sm text-primary-400">
                          Using default content
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-primary-400">
              <div className="text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Select a clause</p>
                <p className="text-sm mt-1">Choose a clause from the list to view details</p>
                <button
                  onClick={() => setShowClauseSuggestions(true)}
                  className="mt-4 flex items-center gap-2 mx-auto px-4 py-2 bg-pink-500/20 text-pink-400 rounded-lg hover:bg-pink-500/30 transition-colors"
                >
                  <Lightbulb className="w-4 h-4" />
                  Get AI Suggestions
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Add Clause Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddClauseModal onClose={() => setShowAddModal(false)} />
        )}
      </AnimatePresence>

      {/* AI Floating Button */}
      <AIFloatingButton
        onOpenChat={() => aiAssistant.open({ currentPage: 'Clause Library' })}
        onOpenClauseSuggestions={() => setShowClauseSuggestions(true)}
        showAnalyzer={false}
        showSmartFill={false}
        showClauseSuggestions={true}
      />

      {/* Command Palette */}
      <CommandPalette 
        isOpen={commandPalette.isOpen} 
        onClose={commandPalette.close}
        onOpenAIAssistant={() => aiAssistant.open({ currentPage: 'Clause Library' })}
        onOpenClauseSuggestions={() => setShowClauseSuggestions(true)}
      />

      {/* AI Assistant Panel */}
      <AIAssistant 
        isOpen={aiAssistant.isOpen} 
        onClose={aiAssistant.close}
        context={aiAssistant.context}
      />

      {/* AI Clause Suggestions Modal */}
      <AIClauseSuggestions
        isOpen={showClauseSuggestions}
        onClose={() => setShowClauseSuggestions(false)}
        documentType={selectedCategory === 'All' ? 'Legal Document' : selectedCategory}
        jurisdiction="Wisconsin"
        existingClauses={clauses.map(c => c.name)}
        onInsertClause={handleInsertClause}
      />
    </div>
  );
}

function AddClauseModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Real Estate');
  const [jurisdictions, setJurisdictions] = useState<string[]>(['WI']);
  const [content, setContent] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Save clause
    console.log({ name, category, jurisdictions, content });
    onClose();
  };

  const toggleJurisdiction = (j: string) => {
    if (jurisdictions.includes(j)) {
      setJurisdictions(jurisdictions.filter(x => x !== j));
    } else {
      setJurisdictions([...jurisdictions, j]);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-2xl bg-primary-800 border border-primary-700 rounded-xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-primary-700">
          <h2 className="text-lg font-semibold text-white">Add New Clause</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/10 rounded transition-colors"
          >
            <X className="w-5 h-5 text-primary-400" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-1">
              Clause Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-field"
              placeholder="e.g., Warranty Covenant"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-1">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="input-field"
            >
              <option value="Real Estate">Real Estate</option>
              <option value="Business">Business</option>
              <option value="Estate Planning">Estate Planning</option>
              <option value="General">General</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-1">
              Jurisdictions
            </label>
            <div className="flex gap-2">
              {['WI', 'FL', 'IL', 'MN'].map(j => (
                <button
                  key={j}
                  type="button"
                  onClick={() => toggleJurisdiction(j)}
                  className={`px-4 py-2 rounded-lg border transition-colors ${
                    jurisdictions.includes(j)
                      ? 'bg-accent-500/20 border-accent-500/50 text-accent-400'
                      : 'bg-primary-900/50 border-primary-600/50 text-primary-400 hover:text-white'
                  }`}
                >
                  {j}
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-1">
              Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="input-field h-48 font-mono text-sm resize-none"
              placeholder="Enter clause content with {{variables}}..."
              required
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t border-primary-700">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Add Clause
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
